// Selettore a cascata Marca → Modello con ricerca, supporta uno o più type codes
import { useState, useEffect, useMemo } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import api from '../../lib/api';
import { Euro, ChevronsUpDown, Check, Loader2, Package } from 'lucide-react';
import { getKeySpec } from './constants';

export function BrandModelSelector({
  typeCode,
  typeCodes,
  selectedItem,
  onSelect,
  disabled = false
}) {
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [models, setModels] = useState([]);
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);
  const [brandOpen, setBrandOpen] = useState(false);
  const [modelOpen, setModelOpen] = useState(false);

  // Risoluzione type codes - supporta typeCode singolo o array typeCodes
  const effectiveTypeCodes = useMemo(() => {
    if (typeCodes && Array.isArray(typeCodes) && typeCodes.length > 0) {
      return typeCodes;
    }
    return typeCode ? [typeCode] : [];
  }, [typeCode, typeCodes]);

  const typeCodesParam = effectiveTypeCodes.join(',');

  // Carica marche per tipologia
  useEffect(() => {
    if (effectiveTypeCodes.length === 0) return;

    const loadBrands = async () => {
      setLoadingBrands(true);
      try {
        const response = await api.get(`/catalog/brands?type_codes=${typeCodesParam}`);
        setBrands(response.data || []);
      } catch (error) {
        console.error('Error loading brands:', error);
      } finally {
        setLoadingBrands(false);
      }
    };

    loadBrands();
  }, [typeCodesParam, effectiveTypeCodes.length]);

  // Carica modelli quando viene selezionata una marca
  useEffect(() => {
    if (effectiveTypeCodes.length === 0 || !selectedBrand) {
      setModels([]);
      return;
    }

    const loadModels = async () => {
      setLoadingModels(true);
      try {
        const params = new URLSearchParams();
        params.append('type_codes', typeCodesParam);
        params.append('brand', selectedBrand);
        params.append('active_only', 'true');

        const response = await api.get(`/catalog/items?${params.toString()}`);
        setModels(response.data.items || []);
      } catch (error) {
        console.error('Error loading models:', error);
      } finally {
        setLoadingModels(false);
      }
    };

    loadModels();
  }, [typeCodesParam, selectedBrand, effectiveTypeCodes.length]);

  // Reset quando selectedItem cambia dall'esterno (es. dopo aggiunta)
  useEffect(() => {
    if (!selectedItem) {
      setSelectedBrand('');
      setModels([]);
    }
  }, [selectedItem]);

  const handleBrandSelect = (brand) => {
    setSelectedBrand(brand);
    onSelect(null); // Reset selezione modello
    setBrandOpen(false);
  };

  const handleModelSelect = (item) => {
    onSelect(item);
    setModelOpen(false);
  };

  // Per i testid usa il primo type code
  const primaryTypeCode = effectiveTypeCodes[0] || 'unknown';

  return (
    <div className="flex gap-2 flex-1">
      {/* Selettore Marca */}
      <Popover open={brandOpen} onOpenChange={setBrandOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={brandOpen}
            disabled={disabled || loadingBrands}
            className="w-[180px] justify-between bg-zinc-50 border-zinc-200 hover:bg-zinc-100 h-12 px-4 rounded-xl text-left font-normal"
            data-testid={`${primaryTypeCode.toLowerCase()}-brand-trigger`}
          >
            {loadingBrands ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : selectedBrand ? (
              <span className="font-medium truncate">{selectedBrand}</span>
            ) : (
              <span className="text-zinc-500">Marca...</span>
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[200px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Cerca marca..." data-testid={`${primaryTypeCode.toLowerCase()}-brand-search`} />
            <CommandList>
              <CommandEmpty>Nessuna marca trovata</CommandEmpty>
              <CommandGroup>
                {brands.map((brand) => (
                  <CommandItem
                    key={brand}
                    value={brand}
                    onSelect={() => handleBrandSelect(brand)}
                    className="cursor-pointer"
                  >
                    <span className="font-medium">{brand}</span>
                    {selectedBrand === brand && (
                      <Check className="ml-auto h-4 w-4 text-emerald-500" />
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Selettore Modello */}
      <Popover open={modelOpen} onOpenChange={setModelOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={modelOpen}
            disabled={disabled || !selectedBrand || loadingModels}
            className="flex-1 justify-between bg-zinc-50 border-zinc-200 hover:bg-zinc-100 h-12 px-4 rounded-xl text-left font-normal"
            data-testid={`${primaryTypeCode.toLowerCase()}-model-trigger`}
          >
            {loadingModels ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : selectedItem ? (
              <span className="flex items-center gap-2 truncate">
                <span className="font-medium">{selectedItem.model}</span>
                {getKeySpec(selectedItem) && (
                  <Badge variant="secondary" className="font-mono text-xs">
                    {getKeySpec(selectedItem)}
                  </Badge>
                )}
              </span>
            ) : !selectedBrand ? (
              <span className="text-zinc-400">Prima seleziona marca</span>
            ) : (
              <span className="text-zinc-500">Seleziona modello...</span>
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[350px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Cerca modello..." data-testid={`${primaryTypeCode.toLowerCase()}-model-search`} />
            <CommandList>
              {loadingModels ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
                </div>
              ) : models.length === 0 ? (
                <CommandEmpty>
                  <Package className="h-8 w-8 mx-auto mb-2 text-zinc-300" />
                  <p className="text-zinc-500">Nessun modello trovato</p>
                </CommandEmpty>
              ) : (
                <CommandGroup>
                  {models.map((item) => {
                    const isHybridItem = item.type_code === 'INVERTER_IBRIDO';
                    return (
                      <CommandItem
                        key={item.id}
                        value={`${item.model} ${isHybridItem ? 'ibrido' : ''}`}
                        onSelect={() => handleModelSelect(item)}
                        className="flex items-center justify-between py-3 cursor-pointer"
                        data-testid={`catalog-item-${item.id}`}
                      >
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-zinc-900">{item.model}</span>
                            {isHybridItem && (
                              <Badge className="text-[10px] px-1.5 py-0 h-4 bg-indigo-100 text-indigo-700 border-0">
                                IBRIDO
                              </Badge>
                            )}
                          </div>
                          <span className="text-xs text-zinc-500 font-mono">{item.item_code}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {getKeySpec(item) && (
                            <Badge variant="outline" className="font-mono text-xs">
                              {getKeySpec(item)}
                            </Badge>
                          )}
                          {item.prezzo_listino && (
                            <span className="text-xs text-zinc-500 flex items-center">
                              <Euro className="h-3 w-3 mr-0.5" />
                              {item.prezzo_listino.toLocaleString('it-IT')}
                            </span>
                          )}
                          {selectedItem?.id === item.id && (
                            <Check className="h-4 w-4 text-emerald-500" />
                          )}
                        </div>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
